// BreakdownGroupModal saf form mantığı (spec §6.3 / §18 DR6): varsayılanlar, boyut → grup adı,
// "Diğer" → grup adından otomatik boyut kodu, `<üst>__<boyut>` kod önizlemesi, Kaydet kapısı ve
// `POST /kpi/metrics/:id/breakdown/groups` gövdesi. Bileşen yalnız görünüm + olay; kural burada.

import {
  KPI_BREAKDOWN_DIMENSIONS,
  KPI_BREAKDOWN_DIMENSION_LABELS,
  KPI_DIMENSION_SLUG_RE,
  breakdownGroupCode,
  defaultBreakdownRollup,
  slugifyKpiCode,
  type KpiBreakdown,
  type KpiBreakdownGroupCreateBody,
  type KpiRootCode,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "./kpi-breakdown-copy";
import { type SearchSelectOption } from "./search-select";

export type BreakdownRollup = KpiBreakdown["rollup"];

/** Grup boyut kodu kuralı (`KpiBreakdownSchema.dimension` ≤40, kod gövdesi) — shared tek kaynak. */
export const DIMENSION_SLUG_RE = KPI_DIMENSION_SLUG_RE;
const OTHER = "diger";

export interface MemberDraft {
  key: string;
  label: string;
  /** "Katalogdan seç" ile seçilen mevcut üye; null = yeni üye */
  metricId: string | null;
}

export interface GroupForm {
  /** `KPI_BREAKDOWN_DIMENSIONS` değeri ya da sözlükteki özel boyut slug'ı; "" = seçilmedi */
  dimension: string;
  /** özel boyutun adı ("Diğer" → kullanıcı yazar; sözlükten seçilen → sözlük etiketi) */
  dimensionLabel: string;
  /** "Diğer" boyut kodu (grup adından üretilir, elle düzenlenebilir) */
  customSlug: string;
  slugTouched: boolean;
  label: string;
  labelTouched: boolean;
  complete: boolean;
  rollup: BreakdownRollup;
  /** müşteriye özel grup / üyeler */
  clientOnly: boolean;
  members: MemberDraft[];
  /** üye satırları kullanıcı tarafından düzenlendi (boyut değişiminde şablon onayı — §19 B5) */
  membersTouched: boolean;
}

export interface GroupFormContext {
  total: { metricId: string; code: string; label: string; unit: string | null };
  /**
   * İkinci seviye (K-B9): grup bir seviye-1 ÜYENİN altına kurulur. `total` o zaman aile KÖKÜdür
   * (etiket/birim satırı için); sunucuya giden üst kimlik `parentMember.metricId`.
   */
  parentMember?: { label: string; groupLabel: string; metricId?: string | undefined } | undefined;
  /** üye ekleme kipi: mevcut grup (metrik); null = yeni grup */
  group: {
    metricId: string;
    label: string;
    breakdown: KpiBreakdown | null;
    clientId: string | null;
  } | null;
  /** aynı üstte zaten olan boyutlar (katalog) — seçenekte pasif */
  usedDimensions: ReadonlySet<string>;
  /** üstün bu setteki hesap grubu (varsa yeni grup doğrulama için: varsayılan "yok") */
  calcGroupLabel: string | null;
  /** toplam müşteriye özelse onun müşterisi — grup da müşteriye özel olur */
  totalClientId: string | null;
  /** toplamın kök kategorisi (boyut listesi bölümleri, §19 B3); yok/bilinmiyor = null */
  rootCode?: KpiRootCode | null | undefined;
}

let seq = 0;
export const newMemberDraft = (): MemberDraft => {
  seq += 1;
  return { key: `m${seq.toString()}`, label: "", metricId: null };
};

export function initialGroupForm(ctx: GroupFormContext): GroupForm {
  return {
    dimension: "",
    dimensionLabel: "",
    customSlug: "",
    slugTouched: false,
    label: "",
    labelTouched: false,
    complete: true,
    rollup: ctx.calcGroupLabel ? "none" : defaultBreakdownRollup(ctx.total.unit),
    clientOnly: ctx.totalClientId !== null,
    members: ctx.group ? [newMemberDraft()] : [newMemberDraft(), newMemberDraft()],
    membersTouched: false,
  };
}

/** Etiketten boyut kodu: kpi-code slug kuralı, 40 karaktere kırpılır. */
export function slugForDimension(label: string): string {
  return slugifyKpiCode(label).slice(0, 40).replace(/_+$/, "");
}

export function setFormDimension(form: GroupForm, dimension: string): GroupForm {
  const next = { ...form, dimension };
  if (!form.labelTouched) {
    next.label =
      dimension === OTHER || dimension === ""
        ? ""
        : (KPI_BREAKDOWN_DIMENSION_LABELS[
            dimension as keyof typeof KPI_BREAKDOWN_DIMENSION_LABELS
          ] ?? "");
  }
  if (dimension === OTHER && !form.slugTouched) next.customSlug = slugForDimension(next.label);
  return next;
}

export function setFormLabel(form: GroupForm, label: string): GroupForm {
  const next = { ...form, label, labelTouched: label !== "" };
  if (form.dimension === OTHER && !form.slugTouched && form.dimensionLabel.trim() === "")
    next.customSlug = slugForDimension(label);
  return next;
}

/** "Diğer" → boyut adı: kod (elle değişmediyse) ve grup adı (elle değişmediyse) ondan türer. */
export function setFormDimensionLabel(form: GroupForm, dimensionLabel: string): GroupForm {
  const next = { ...form, dimensionLabel };
  if (!form.slugTouched) next.customSlug = slugForDimension(dimensionLabel || form.label);
  if (!form.labelTouched) next.label = dimensionLabel;
  return next;
}

/** Hazır listede olmayan (Diğer ya da sözlükten seçilmiş özel) boyut mu. */
export function isCustomDimension(form: GroupForm): boolean {
  return (
    form.dimension !== "" &&
    (form.dimension === OTHER ||
      !(KPI_BREAKDOWN_DIMENSIONS as readonly string[]).includes(form.dimension))
  );
}

/**
 * Gönderilecek özel boyut adı (sözlükte görünen ad; boşsa grup adı, şema tavanı 80) — hazır
 * boyutta null. Grup kaydı ve "Kırılıma dönüştür" gövdesi AYNI kuralı kullanır.
 */
export function customDimensionLabelOf(form: GroupForm): string | null {
  if (!isCustomDimension(form)) return null;
  const label = (form.dimensionLabel.trim() || form.label.trim()).slice(0, 80).trim();
  return label === "" ? null : label;
}

/** Gönderilecek boyut kodu ("" = henüz yok). */
export function dimensionSlug(form: GroupForm): string {
  return form.dimension === OTHER ? form.customSlug.trim() : form.dimension;
}

export function groupCodePreview(ctx: GroupFormContext, form: GroupForm): string | null {
  const dim = dimensionSlug(form);
  return dim && DIMENSION_SLUG_RE.test(dim) ? breakdownGroupCode(ctx.total.code, dim) : null;
}

/** Boyut seçenekleri (12 + Diğer); aynı üstte mevcut boyut pasif. "Diğer" her zaman açık. */
export function dimensionOptions(used: ReadonlySet<string>): SearchSelectOption[] {
  return KPI_BREAKDOWN_DIMENSIONS.map((d) => {
    const taken = d !== OTHER && used.has(d);
    return {
      value: d,
      label: KPI_BREAKDOWN_DIMENSION_LABELS[d],
      code: d,
      ...(taken ? { disabled: true, hint: KPI_BREAKDOWN_COPY.modal.dimensionUsed } : {}),
    };
  });
}

const filledMembers = (form: GroupForm): MemberDraft[] =>
  form.members.filter((m) => m.metricId !== null || m.label.trim() !== "");

/** Kaydet ("Önizle ve kaydet") kapısı: ilk eksik koşulun metni. */
export function groupFormGate(
  ctx: GroupFormContext,
  form: GroupForm,
): { ok: boolean; reason: string | null } {
  const C = KPI_BREAKDOWN_COPY.modal;
  const fail = (reason: string) => ({ ok: false, reason });
  if (!ctx.group) {
    const dim = dimensionSlug(form);
    if (form.dimension === "") return fail(C.needDimension);
    if (form.dimension === OTHER && form.dimensionLabel.trim() === "" && form.label.trim() === "")
      return fail(C.needDimensionName);
    if (!DIMENSION_SLUG_RE.test(dim)) return fail(C.customDimensionInvalid);
    if (ctx.usedDimensions.has(dim)) return fail(KPI_BREAKDOWN_COPY.errors.duplicate_dimension);
    if (form.label.trim() === "") return fail(C.needLabel);
  }
  const members = filledMembers(form);
  const need = ctx.group ? 1 : form.complete ? 2 : 1;
  if (members.length < need) return fail(C.needMembers(need));
  const names = members.map((m) => m.label.trim().toLocaleLowerCase("tr"));
  if (new Set(names).size !== names.length) return fail(C.duplicateMemberLabel);
  return { ok: true, reason: null };
}

export function buildGroupBody(
  ctx: GroupFormContext,
  form: GroupForm,
  /** `setId` null = katalog (MetricModal "Yeni grup…") — grup + üyeler hiçbir sete eklenmez */
  opts: { setId: string | null; clientId: string | null },
  dryRun: boolean,
): KpiBreakdownGroupCreateBody {
  const setPart = opts.setId ? { setId: opts.setId } : {};
  const members = filledMembers(form).map((m) =>
    m.metricId !== null ? { metricId: m.metricId } : { label: m.label.trim() },
  );
  const clientId = form.clientOnly ? (ctx.totalClientId ?? opts.clientId) : null;
  if (ctx.group) {
    return { ...setPart, groupId: ctx.group.metricId, clientId, members, dryRun };
  }
  // şema tavanı 80 (grup adı 200'e kadar olabilir — yedek kırpılır)
  const dimensionLabel = customDimensionLabelOf(form);
  return {
    ...setPart,
    dimension: dimensionSlug(form),
    ...(dimensionLabel ? { dimensionLabel } : {}),
    label: form.label.trim(),
    complete: form.complete,
    rollup: form.rollup,
    clientId,
    members,
    dryRun,
  };
}

export function setFormCustomSlug(form: GroupForm, slug: string): GroupForm {
  return { ...form, customSlug: slug, slugTouched: true };
}
